// Implements: F-UIX-018, F-USG-005, F-USG-006
/**
 * The reads of "Consumo por ámbito", cached per organization and team (UIX.5, checklist 14).
 *
 * Every key carries the ids of the target it was asked for (and the grouping), so an answer
 * is only ever read back under the organization or team it belongs to: switching from one
 * team to another while the first one's answer is still on its way never shows it on the
 * second's screen, and a stale answer is ignored by id.
 */

import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type {
  ConsumptionGrouping,
  OrganizationConsumptionView,
  ReportTarget,
  ScopeMockupsView,
} from "../../../types/reports";
import { fetchOrganizationTeams, fetchScopeConsumption, fetchScopeMockups } from "../api/scopeReportsApi";
import type { TeamListView } from "../api/scopeReportsApi";

/** The ids a target's reads are cached under. */
function idsOf(target: ReportTarget): readonly string[] {
  return target.kind === "organization"
    ? ["organization", target.organizationId]
    : ["team", target.organizationId, target.teamId];
}

export function scopeReportKey(
  target: ReportTarget,
  part: "consumption" | "mockups",
  grouping?: ConsumptionGrouping,
): readonly string[] {
  return ["backoffice", ...idsOf(target), part, ...(grouping === undefined ? [] : [grouping])];
}

export function organizationTeamsKey(organizationId: string): readonly string[] {
  return ["backoffice", "organization", organizationId, "teams"];
}

export interface ScopeReads {
  readonly consumption: UseQueryResult<OrganizationConsumptionView, ApiError>;
  /**
   * The same report by day, for the daily chart: whatever the table is grouped by. When the table
   * is by day it is the very same cache entry, so nothing is asked twice.
   */
  readonly daily: UseQueryResult<OrganizationConsumptionView, ApiError>;
  readonly mockups: UseQueryResult<ScopeMockupsView, ApiError>;
  /** The organization's teams, to name team rows; never asked for a team target. */
  readonly teams: UseQueryResult<TeamListView, ApiError>;
}

export function useScopeReports(target: ReportTarget, grouping: ConsumptionGrouping): ScopeReads {
  const consumption = useQuery<OrganizationConsumptionView, ApiError>({
    queryKey: scopeReportKey(target, "consumption", grouping),
    queryFn: ({ signal }) => fetchScopeConsumption(target, grouping, signal),
  });
  const daily = useQuery<OrganizationConsumptionView, ApiError>({
    queryKey: scopeReportKey(target, "consumption", "day"),
    queryFn: ({ signal }) => fetchScopeConsumption(target, "day", signal),
  });
  const mockups = useQuery<ScopeMockupsView, ApiError>({
    queryKey: scopeReportKey(target, "mockups"),
    queryFn: ({ signal }) => fetchScopeMockups(target, signal),
  });
  const teams = useQuery<TeamListView, ApiError>({
    queryKey: organizationTeamsKey(target.organizationId),
    queryFn: ({ signal }) => fetchOrganizationTeams(target.organizationId, signal),
    // Only the organization's admin may list its teams, and only team rows need names.
    enabled: target.kind === "organization" && grouping === "team",
  });
  return { consumption, daily, mockups, teams };
}
